<script setup lang="ts">
import { ref, watch } from 'vue'

import CampaignFormPriorityList from '@/components/CampaignForm/CampaignFormPriority/CampaignFormPriorityList.vue'
import type { CampaignFormType } from '@/lib/types/campaign.types'
import { BoxComponent, FlexComponent, ImageComponent, RadioButton, RadioButtonGroupComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  advertiserList: CampaignFormType['advertiserList']
  currentCampaignName: string
  trafficSourceCosts: CampaignFormType['trafficSourceCosts']
  currency?: string
  campaignId?: number
  isEditMode?: boolean
}>()
const priority = defineModel<Record<number, number | null | undefined>>('priority')
const currentAdvertiserId = ref<number>()

watch(
  () => props.advertiserList,
  () => {
    const newPriority = { ...priority.value }
    const advertiserIds = props.advertiserList.map((item) => item.id)
    if (props.advertiserList.length > 0) {
      let shouldCurrentAdvertiserIdChanged = true

      advertiserIds.forEach((id) => {
        if (currentAdvertiserId.value === id) {
          shouldCurrentAdvertiserIdChanged = false
        }
        // Priority list contains only active campaigns: during the editing we don't want to pass priority (if it hasn't changed) for active campaigns,
        // and okay to pass as top priority for inactive
        // During the creation we want to pass null as a valid value if it hasn't changed
        if (!(id in newPriority)) {
          newPriority[id] = props.isEditMode ? undefined : null
        }
      })

      if (shouldCurrentAdvertiserIdChanged) {
        currentAdvertiserId.value = props.advertiserList[0]?.id
      }

      Object.keys(newPriority).forEach((index: string) => {
        const id = Number(index)
        if (!advertiserIds.includes(id)) {
          delete newPriority[id]
        }
      })

      priority.value = newPriority
    }
  },
  { immediate: true },
)
</script>
<template>
  <div v-if="advertiserList.length > 0">
    <RadioButtonGroupComponent v-model="currentAdvertiserId" children-width="0%">
      <RadioButton v-for="item in advertiserList" :key="item.id" :value="item.id" :label="item.label">
        <template #content>
          <FlexComponent :gap="8" align="center">
            <FlexComponent v-if="item.logo" is-child grow="1">
              <BoxComponent :w="48" :h="24">
                <ImageComponent
                  v-if="item.logo"
                  :image="item.logo"
                  :width="48"
                  :height="24"
                  class="campaign-form-priority__advertiser-logo"
                  object-fit="contain"
                  resize-type="fit"
                  :alt="item.label"
                />
              </BoxComponent>
            </FlexComponent>
            <FlexComponent is-child grow="1">
              <TypographyComponent variant="label-large" class="campaign-form-priority__advertiser-title">{{
                item.label
              }}</TypographyComponent>
            </FlexComponent>
          </FlexComponent>
        </template>
      </RadioButton>
    </RadioButtonGroupComponent>
    <BoxComponent :mt="24">
      <div
        v-for="advertiser in advertiserList"
        :key="advertiser.id"
        class="campaign-form-priority__list"
        :class="{ 'campaign-form-priority__list-active': advertiser.id === currentAdvertiserId }"
      >
        <template v-if="priority && advertiser.id in priority">
          <CampaignFormPriorityList
            v-model:priority-item="priority[advertiser.id]"
            :campaign-id="campaignId"
            :advertiser="advertiser"
            :current-campaign-name="currentCampaignName"
            :traffic-source-costs="trafficSourceCosts"
            :currency="currency"
            :should-add-new-item="!isEditMode"
          />
        </template>
      </div>
    </BoxComponent>
  </div>
  <FlexComponent v-else justify="center">
    <TypographyComponent variant="title-large" class="campaign-form-priority__placeholder">
      Select advertiser(s) first
    </TypographyComponent>
  </FlexComponent>
</template>
<style lang="scss" scoped>
.campaign-form-priority__advertiser-logo {
  border-radius: 4px;
}
.campaign-form-priority__advertiser-title {
  line-height: 24px;
}
.campaign-form-priority__placeholder {
  font-style: italic;
  color: var(--secondary-64);
}
.campaign-form-priority__list {
  width: 100%;
  display: none;
  opacity: 0;
  transition: opacity 1s ease-in-out;
}
.campaign-form-priority__list-active {
  display: block;
  opacity: 1;
}
:deep(.el-radio-button__inner.el-radio-button__inner) {
  padding: 7px 16px;
  line-height: 24px;
  border-radius: 40px;
}
</style>
